<script setup lang="ts">
import type { RadioGroupRootEmits, RadioGroupRootProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { RadioGroupRoot, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/utils/shadcn/lib/utils.ts';
import ItemCard from './internal/RadioGroupItemCard.vue';
import ItemText from './internal/RadioGroupItemText.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<RadioGroupRootProps & {
    class?: HTMLAttributes['class']
    orientation?: 'vertical' | 'horizontal'
    variant?: 'default' | 'card'
    options?: RadioOption[]
    required?: boolean
    disabled?: boolean
    invalid?: boolean
}>(), {
    class: '',
    orientation: 'vertical',
    variant: 'default',
    options: () => [],
    required: false,
    disabled: false,
    invalid: false,
});

const emits = defineEmits<RadioGroupRootEmits>();

interface RadioOption {
    value: any
    label: string
    description?: string
}

const delegatedProps = reactiveOmit(props, 'class', 'options');

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <RadioGroupRoot
        v-slot="slotProps"
        v-bind="forwarded"
        data-slot="radio-group"
        class="group/radio-group"
        :aria-invalid="props.invalid ? 'true' : undefined"
        :class="cn(
            'py-2',
            props.orientation === 'horizontal'
                ? 'flex flex-row gap-5 items-center justify-start'
                : 'grid gap-2',
            props.class,
        )"
    >
        <template
            v-for="option in props.options"
            :key="option.value"
        >
            <slot
                name="option"
                v-bind="{ ...option, checked: slotProps.modelValue === option.value }"
            >
                <ItemText
                    v-if="props.variant === 'default'"
                    v-bind="option"
                    :checked="slotProps.modelValue === option.value"
                    class="flex items-center gap-2"
                />
                <ItemCard
                    v-else-if="props.variant === 'card'"
                    v-bind="option"
                    :checked="slotProps.modelValue === option.value"
                    class="flex items-center gap-2"
                />
            </slot>
        </template>
    </RadioGroupRoot>
</template>
